<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import BrandIcon from 'dashboard/components/widgets/BrandIcon.vue';
import CustomTriggerForm from './CustomTriggerForm.vue';
import { useCustomTriggers } from './useCustomTriggers';

const props = defineProps({
  trigger: { type: Object, required: true },
  botName: { type: String, default: 'SALESBOT' },
  availableInboxes:  { type: Array, default: () => [] },
  availableLabels:   { type: Array, default: () => [] },
  availableAgents:   { type: Array, default: () => [] },
  availablePipelines:{ type: Array, default: () => [] },
});
const emit = defineEmits(['save', 'close', 'delete']);

// ════════════ DISPARADORES por intención ════════════
const EVENT_GROUPS = [
  { key: 'new_lead', label: 'Cuando un lead NUEVO llega', icon: '🚀', headerBg: 'bg-emerald-50', headerText: 'text-emerald-800',
    options: [
      { key: 'first_message',         icon: '💬', label: 'Primer mensaje en WhatsApp / IG / canal',  trigger_type: 'message_received', extra: { first_message: true } },
      { key: 'email_received',        icon: '📧', label: 'Email recibido',                           trigger_type: 'message_received', extra: { channel: 'email' } },
      { key: 'form_submitted',        icon: '📝', label: 'Formulario web enviado',                   trigger_type: 'webhook',          extra: { event: 'form_submit' } },
      { key: 'lead_created',          icon: '🆕', label: 'Lead creado en una etapa',                 trigger_type: 'stage_created',    extra: {} },
      { key: 'lead_created_or_moved', icon: '📍', label: 'Lead creado o movido a una etapa',         trigger_type: 'stage_any',        extra: {} },
      { key: 'ig_mention',            icon: '📸', label: 'Mención en historia de Instagram',         trigger_type: 'message_received', extra: { source: 'instagram_story_mention' } },
    ] },
  { key: 'lead_advances', label: 'Cuando un lead AVANZA', icon: '🏃', headerBg: 'bg-blue-50', headerText: 'text-blue-800',
    options: [
      { key: 'stage_changed', icon: '➡️', label: 'Lead movido a una etapa',           trigger_type: 'stage_changed', extra: {} },
      { key: 'message_in_stage', icon: '✍️', label: 'Lead escribe estando en una etapa', trigger_type: 'message_received', extra: {}, autoStageCondition: true },
      { key: 'first_message_in_stage', icon: '🎯', label: 'Primer mensaje del cliente en una etapa', trigger_type: 'message_received', extra: { first_in_stage: true }, autoStageCondition: true },
      { key: 'tag_added',     icon: '🏷️', label: 'Etiqueta agregada al lead',         trigger_type: 'tag_added',     extra: {} },
      { key: 'field_updated', icon: '📋', label: 'Campo del lead actualizado',         trigger_type: 'webhook',       extra: { event: 'field_updated' }, soon: true },
    ] },
  { key: 'lead_cold', label: 'Cuando un lead se ENFRÍA', icon: '❄️', headerBg: 'bg-orange-50', headerText: 'text-orange-800',
    options: [
      { key: 'no_response_hours', icon: '💤', label: 'N horas sin respuesta del cliente',       trigger_type: 'webhook', extra: { event: 'idle_hours' },   soon: true },
      { key: 'no_activity_days',  icon: '⏳', label: 'N días sin actividad',                    trigger_type: 'webhook', extra: { event: 'idle_days' },    soon: true },
      { key: 'closed_ago',        icon: '✅', label: 'Conversación cerrada hace N tiempo',      trigger_type: 'webhook', extra: { event: 'resolved_ago' }, soon: true },
    ] },
  { key: 'client_action', label: 'Acción puntual del cliente', icon: '📞', headerBg: 'bg-violet-50', headerText: 'text-violet-800',
    options: [
      { key: 'call_received',    icon: '📞', label: 'Llamada recibida / perdida',           trigger_type: 'webhook',          extra: { event: 'missed_call' },       soon: true },
      { key: 'message_read',     icon: '👁️', label: 'Mensaje saliente leído por el cliente', trigger_type: 'webhook',          extra: { event: 'message_read' },      soon: true },
      { key: 'comment_post',     icon: '💭', label: 'Comentario en post de IG/FB',          trigger_type: 'message_received', extra: { comment_only: true } },
      { key: 'website_visit',    icon: '🌐', label: 'Sitio web visitado',                   trigger_type: 'webhook',          extra: { event: 'website_visit' },     soon: true },
      { key: 'assignee_changed', icon: '👤', label: 'Responsable cambiado',                 trigger_type: 'webhook',          extra: { event: 'assignee_changed' },  soon: true },
    ] },
  { key: 'scheduled', label: 'Recordatorios programados', icon: '⏰', headerBg: 'bg-amber-50', headerText: 'text-amber-800',
    options: [
      { key: 'specific_date', icon: '📅', label: 'En fecha y hora específica',           trigger_type: 'webhook', extra: { event: 'specific_date' }, soon: true },
      { key: 'daily',         icon: '🔁', label: 'Diariamente a una hora',               trigger_type: 'webhook', extra: { event: 'daily' },         soon: true },
      { key: 'before_event',  icon: '🎂', label: 'N días antes de cumpleaños / evento',  trigger_type: 'webhook', extra: { event: 'before_event' },  soon: true },
    ] },
];
// ════════════ Custom triggers (Fase 1: localStorage por cuenta) ════════════
const { triggers: customTriggers, saveTrigger: saveCustomTrigger, deleteTrigger: deleteCustomTrigger } = useCustomTriggers();
const showCustomForm = ref(false);
const editingCustom = ref(null); // null = creating, object = editing

// Convierte un custom trigger a una option estilo system (para renderizar en dropdown)
function customToOption(t) {
  return {
    key: `custom:${t.id}`,
    icon: t.icon,
    label: t.name,
    trigger_type: t.backend_type,
    extra: t.config || {},
    isCustom: true,
    customId: t.id,
  };
}

// Grupos finales: system + (Personalizados, si hay)
const MERGED_GROUPS = computed(() => {
  const groups = EVENT_GROUPS.map(g => ({ ...g, options: g.options.map(o => ({ ...o })) }));
  if (customTriggers.value.length) {
    groups.push({
      key: 'custom_personal',
      label: 'Personalizados',
      icon: '✨',
      headerBg: 'bg-violet-50',
      headerText: 'text-violet-800',
      options: customTriggers.value.map(customToOption),
    });
  }
  return groups;
});
const ALL_OPTIONS = computed(() =>
  MERGED_GROUPS.value.flatMap(g => g.options.map(o => ({ ...o, group: g })))
);

// ════════════ CONDICIONES configurables ════════════
const CONDITION_TYPES = [
  { key: 'tags',      label: 'Etiquetas (o):',  icon: '🏷️' },
  { key: 'channel',   label: 'Canal:',          icon: '📡' },
  { key: 'stage',     label: 'Etapa del embudo:', icon: '📊' },
  { key: 'assignee',  label: 'Usuario responsable:', icon: '👤' },
  { key: 'custom',    label: 'Campo personalizado:', icon: '📋' },
];

// ════════════ STATE ════════════
const eventKey = ref('first_message');
const conditions = ref([]); // [{ type: 'tags', values: [1,2] }, ...]
const hoursMode = ref('always'); // 'always' | 'custom'
const hoursDays = ref([1,2,3,4,5,6,7]); // 1=Lu...7=Do
const hoursFrom = ref('09:00');
const hoursTo   = ref('18:00');
const leaveUnanswered = ref(false);
const isActive = ref(true);

const showEventDropdown = ref(false);
const eventSearch = ref('');
const showAddCondition = ref(false);
const openValuePickerIdx = ref(null); // index del condition cuyo value picker está abierto

const selectedOption = computed(() => ALL_OPTIONS.value.find(o => o.key === eventKey.value) || ALL_OPTIONS.value[0]);
const filteredGroups = computed(() => {
  const q = eventSearch.value.trim().toLowerCase();
  if (!q) return MERGED_GROUPS.value;
  return MERGED_GROUPS.value.map(g => ({ ...g, options: g.options.filter(o => o.label.toLowerCase().includes(q)) })).filter(g => g.options.length);
});

watch(() => props.trigger, (t) => {
  const cfg = t.config || {};
  const tt = t.trigger_type;
  // 1) Si el config tiene event_key (guardado en versiones nuevas), usar match exacto
  let found = cfg.event_key ? ALL_OPTIONS.value.find(o => o.key === cfg.event_key) : null;
  // 2) Fallback: buscar por trigger_type + extras (compatibilidad con triggers viejos sin event_key)
  if (!found) {
    found = ALL_OPTIONS.value.find(o => {
      if (o.trigger_type !== tt) return false;
      const extraKeys = Object.keys(o.extra || {});
      // Si el option tiene extras específicos, deben matchear todos
      if (extraKeys.length > 0) return extraKeys.every(k => cfg[k] === o.extra[k]);
      // Si no tiene extras, lo dejamos como último recurso (no match aquí — fallback final abajo)
      return false;
    });
  }
  eventKey.value = found?.key || 'first_message';
  conditions.value = Array.isArray(cfg.conditions) ? JSON.parse(JSON.stringify(cfg.conditions)) : [];
  hoursMode.value = cfg.hours_mode || 'always';
  hoursDays.value = Array.isArray(cfg.hours_days) ? cfg.hours_days : [1,2,3,4,5,6,7];
  hoursFrom.value = cfg.hours_from || '09:00';
  hoursTo.value   = cfg.hours_to   || '18:00';
  leaveUnanswered.value = Boolean(cfg.leave_unanswered);
  isActive.value = t.active !== false;
}, { immediate: true });

// ════════════ HANDLERS ════════════
function pickEvent(option) {
  eventKey.value = option.key;
  showEventDropdown.value = false;
  eventSearch.value = '';
  // Si el trigger requiere una etapa (autoStageCondition), agregar la condition
  // automáticamente si el usuario aún no tiene una de tipo 'stage'.
  if (option.autoStageCondition && !conditions.value.some(c => c.type === 'stage')) {
    conditions.value.push({ type: 'stage', values: [] });
    openValuePickerIdx.value = conditions.value.length - 1;
  }
}

// ── Handlers de custom triggers ──
function openCreateCustom() {
  editingCustom.value = null;
  showCustomForm.value = true;
  showEventDropdown.value = false;
}

function openEditCustom(opt) {
  const found = customTriggers.value.find(t => t.id === opt.customId);
  if (!found) return;
  editingCustom.value = JSON.parse(JSON.stringify(found));
  showCustomForm.value = true;
  showEventDropdown.value = false;
}

function onCustomSaved(payload) {
  saveCustomTrigger(payload);
  showCustomForm.value = false;
  // Auto-seleccionar el nuevo/editado trigger
  const id = payload.id || customTriggers.value[customTriggers.value.length - 1]?.id;
  if (id) eventKey.value = `custom:${id}`;
}

function onCustomDelete(opt) {
  // Borrado instantáneo · sin confirm (regla del editor de Victor).
  deleteCustomTrigger(opt.customId);
  // Si estaba seleccionado, volver al default
  if (eventKey.value === opt.key) eventKey.value = 'first_message';
}

function addCondition(type) {
  conditions.value.push({ type, values: [] });
  showAddCondition.value = false;
  openValuePickerIdx.value = conditions.value.length - 1; // abrir picker del nuevo
}

function removeCondition(idx) {
  conditions.value.splice(idx, 1);
}

function toggleConditionValue(idx, val) {
  const c = conditions.value[idx];
  const vIdx = c.values.findIndex(v => v == val);
  if (vIdx >= 0) c.values.splice(vIdx, 1);
  else c.values.push(val);
}

function setCustomValue(idx, value) {
  conditions.value[idx].values = [value];
}

function valuesLabel(c) {
  if (c.type === 'tags') {
    return c.values.map(v => props.availableLabels.find(l => l.id == v || l.title == v)?.title || v).join(', ') || 'Selecciona etiquetas';
  }
  if (c.type === 'channel') {
    return c.values.map(v => props.availableInboxes.find(i => i.id == v)?.name || v).join(', ') || 'Selecciona canales';
  }
  if (c.type === 'stage') {
    return c.values.map(v => {
      for (const p of props.availablePipelines) {
        const s = (p.stages || []).find(st => st.id == v);
        if (s) return `${p.name} › ${s.name}`;
      }
      return v;
    }).join(' · ') || 'Selecciona etapas';
  }
  if (c.type === 'assignee') {
    return c.values.map(v => props.availableAgents.find(a => a.id == v)?.name || v).join(', ') || 'Selecciona agentes';
  }
  if (c.type === 'custom') {
    return c.values[0] || 'Escribe un valor';
  }
  return '';
}

function brandFor(inbox) {
  const t = (inbox.channel_type || '').split('::').pop().toLowerCase();
  if (t === 'api' && /whats|wa\b/i.test(inbox.name || '')) return 'whatsapp';
  if (t === 'whatsapp')      return 'whatsapp';
  if (t === 'instagram')     return 'instagram';
  if (t === 'facebookpage')  return 'facebook';
  if (t === 'tiktok')        return 'tiktok';
  if (t === 'telegram')      return 'telegram';
  if (t === 'email')         return 'email';
  if (t === 'webwidget')     return 'website';
  return 'api';
}

// ════════════ HORAS ACTIVAS ════════════
const DAYS = [
  { v: 1, l: 'Lu' }, { v: 2, l: 'Ma' }, { v: 3, l: 'Mi' },
  { v: 4, l: 'Ju' }, { v: 5, l: 'Vi' }, { v: 6, l: 'Sá' }, { v: 7, l: 'Do' },
];

function toggleDay(d) {
  const idx = hoursDays.value.indexOf(d);
  if (idx >= 0) hoursDays.value.splice(idx, 1);
  else hoursDays.value.push(d);
}

const hoursPresets = [
  { key: 'always',     label: 'Siempre' },
  { key: 'custom',     label: 'Horario personalizado' },
];

const customHoursLabel = computed(() => {
  if (hoursMode.value === 'always') return 'Siempre';
  const days = DAYS.filter(d => hoursDays.value.includes(d.v)).map(d => d.l).join('-');
  return `${days || 'Sin días'} · ${hoursFrom.value} a ${hoursTo.value}`;
});

// ════════════ SAVE ════════════
function save() {
  const opt = selectedOption.value;
  const config = {
    event_key: opt?.key,                  // ← identificador exacto del option (para reload preciso)
    ...(opt?.extra || {}),
    conditions: conditions.value,
    hours_mode: hoursMode.value,
    hours_days: hoursDays.value,
    hours_from: hoursFrom.value,
    hours_to:   hoursTo.value,
    leave_unanswered: leaveUnanswered.value,
  };
  emit('save', {
    trigger: props.trigger,
    payload: { trigger_type: opt?.trigger_type || 'message_received', config, active: isActive.value },
  });
}

function onKeydown(ev) {
  if (ev.key !== 'Escape') return;
  if (showEventDropdown.value) showEventDropdown.value = false;
  else if (showAddCondition.value) showAddCondition.value = false;
  else if (openValuePickerIdx.value !== null) openValuePickerIdx.value = null;
  else emit('close');
}
onMounted(() => document.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-auto"
    style="background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(8px) saturate(160%); -webkit-backdrop-filter: blur(8px) saturate(160%);"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-background rounded-xl shadow-2xl border border-n-weak w-full max-w-xl my-auto"
      style="animation: modalIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);"
    >
      <!-- Header -->
      <div class="px-6 pt-5 pb-2">
        <h2 class="text-lg font-semibold text-n-slate-12">
          Condición de ejecución <span class="uppercase">{{ botName }}</span>
        </h2>
        <p class="text-sm text-n-slate-11 mt-1">Lanza automáticamente bots según las reglas que establezcas a continuación</p>
      </div>

      <!-- Body -->
      <div class="px-6 py-4 space-y-4">

        <!-- ═══ Cuando esto ocurre ═══ -->
        <div>
          <label class="block text-sm text-n-slate-12 mb-1.5">Cuando esto ocurre:</label>
          <div v-on-click-outside="() => (showEventDropdown = false)" class="relative">
            <button type="button"
                    class="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 text-left hover:border-blue-300"
                    @click="showEventDropdown = !showEventDropdown">
              <span class="text-base">{{ selectedOption?.icon }}</span>
              <span class="flex-1 truncate">{{ selectedOption?.label }}</span>
              <span class="text-n-slate-11 text-xs">{{ showEventDropdown ? '▴' : '▾' }}</span>
            </button>

            <div v-if="showEventDropdown"
                 class="absolute top-full left-0 right-0 mt-1 z-20 bg-n-background border border-n-weak rounded-lg shadow-xl overflow-hidden max-h-[400px] flex flex-col">
              <div class="px-3 py-2 border-b border-n-weak">
                <input v-model="eventSearch" type="text" placeholder="Buscar disparador…"
                       class="w-full text-xs px-2 py-1.5 rounded-md bg-n-alpha-black1 border border-n-weak text-n-slate-12 focus:outline-none focus:border-blue-400 placeholder-n-slate-11"
                       @click.stop />
              </div>
              <div class="overflow-y-auto flex-1">
                <template v-for="group in filteredGroups" :key="group.key">
                  <div class="px-3 py-2 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5"
                       :class="[group.headerBg, group.headerText]">
                    <span>{{ group.icon }}</span>{{ group.label }}
                  </div>
                  <div v-for="opt in group.options" :key="opt.key"
                       class="flex items-center gap-1 border-b border-n-weak last:border-b-0 hover:bg-blue-50 dark:hover:bg-blue-500/10 group/opt"
                       :class="opt.key === eventKey ? 'bg-blue-50 dark:bg-blue-500/10' : ''">
                    <button class="flex-1 flex items-center gap-2.5 px-3 py-2 text-sm text-left text-n-slate-12"
                            :class="opt.key === eventKey ? 'font-medium' : ''"
                            @click="pickEvent(opt)">
                      <span class="w-5 text-base">{{ opt.icon }}</span>
                      <span class="flex-1">{{ opt.label }}</span>
                      <span v-if="opt.soon" class="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Próximamente</span>
                      <span v-if="opt.key === eventKey" class="text-blue-600 text-xs">✓</span>
                    </button>
                    <!-- Acciones de custom (edit/delete on hover) -->
                    <template v-if="opt.isCustom">
                      <button type="button" class="opacity-0 group-hover/opt:opacity-100 px-2 py-1 text-n-slate-11 hover:text-blue-600 text-xs"
                              title="Editar disparador"
                              @click.stop="openEditCustom(opt)">✏️</button>
                      <button type="button" class="opacity-0 group-hover/opt:opacity-100 px-2 py-1 text-n-slate-11 hover:text-rose-500 text-xs mr-1"
                              title="Eliminar disparador"
                              @click.stop="onCustomDelete(opt)">🗑️</button>
                    </template>
                  </div>
                </template>
                <!-- Botón crear personalizado -->
                <button type="button"
                        class="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-500/10 border-t border-n-weak font-medium"
                        @click.stop="openCreateCustom">
                  <span class="w-5 text-base">✨</span>
                  <span class="flex-1">Crear disparador personalizado</span>
                  <span class="text-[10px] text-violet-500">localStorage</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Form inline para custom triggers -->
          <div v-if="showCustomForm" class="absolute z-30 mt-2 left-0 right-0">
            <CustomTriggerForm :initial="editingCustom" @save="onCustomSaved" @cancel="showCustomForm = false" />
          </div>
        </div>

        <!-- ═══ Para todos los leads con ═══ -->
        <div>
          <label class="block text-sm text-n-slate-12 mb-1.5">Para todos los leads con:</label>

          <!-- Lista de condiciones -->
          <div class="space-y-2">
            <div v-for="(c, idx) in conditions" :key="idx" class="flex items-start gap-2">
              <div class="flex-1 relative" v-on-click-outside="() => { if (openValuePickerIdx === idx) openValuePickerIdx = null }">
                <button type="button"
                        class="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 text-left hover:border-blue-300"
                        @click="openValuePickerIdx = openValuePickerIdx === idx ? null : idx">
                  <span class="text-n-slate-11 inline-flex items-center gap-1">
                    <span>{{ idx === 0 ? '' : 'Y' }}</span>
                    <span>{{ CONDITION_TYPES.find(t => t.key === c.type)?.icon }}</span>
                    <span class="font-medium text-n-slate-12">{{ CONDITION_TYPES.find(t => t.key === c.type)?.label }}</span>
                  </span>
                  <span class="flex-1 truncate text-n-slate-12">{{ valuesLabel(c) }}</span>
                  <span class="text-n-slate-11 text-xs">{{ openValuePickerIdx === idx ? '▴' : '▾' }}</span>
                </button>

                <!-- Popover de valores según tipo -->
                <div v-if="openValuePickerIdx === idx"
                     class="absolute top-full left-0 right-0 mt-1 z-30 bg-n-background border border-n-weak rounded-lg shadow-xl max-h-[260px] overflow-y-auto p-1.5">
                  <!-- Etiquetas -->
                  <template v-if="c.type === 'tags'">
                    <button v-for="lab in availableLabels" :key="lab.id"
                            class="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-n-alpha-black1 text-sm text-left"
                            @click="toggleConditionValue(idx, lab.id)">
                      <span class="w-3.5 h-3.5 rounded border" :style="{ borderColor: lab.color || '#cbd5e1', backgroundColor: c.values.includes(lab.id) ? (lab.color || '#3b82f6') : 'transparent' }" />
                      <span class="text-n-slate-12">{{ lab.title }}</span>
                    </button>
                    <div v-if="!availableLabels.length" class="text-xs text-n-slate-11 text-center py-3">No hay etiquetas creadas</div>
                  </template>

                  <!-- Canales -->
                  <template v-else-if="c.type === 'channel'">
                    <button v-for="inbox in availableInboxes" :key="inbox.id"
                            class="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-n-alpha-black1 text-sm text-left"
                            @click="toggleConditionValue(idx, inbox.id)">
                      <span class="w-4 h-4 inline-flex items-center justify-center" :class="c.values.includes(inbox.id) ? 'text-blue-600' : 'text-transparent'">
                        <span class="i-lucide-check w-3.5 h-3.5" />
                      </span>
                      <BrandIcon :channel="brandFor(inbox)" :size="20" />
                      <span class="text-n-slate-12 truncate">{{ inbox.name }}</span>
                    </button>
                  </template>

                  <!-- Etapas (agrupadas por pipeline) -->
                  <template v-else-if="c.type === 'stage'">
                    <template v-for="pipeline in availablePipelines" :key="pipeline.id">
                      <div class="px-2 py-1.5 text-[11px] uppercase tracking-wider text-n-slate-11 font-bold bg-n-alpha-black1">{{ pipeline.name }}</div>
                      <button v-for="stage in (pipeline.stages || [])" :key="stage.id"
                              class="w-full flex items-center gap-2 px-3 py-1.5 rounded hover:bg-n-alpha-black1 text-sm text-left"
                              @click="toggleConditionValue(idx, stage.id)">
                        <span class="w-4 h-4 inline-flex items-center justify-center" :class="c.values.includes(stage.id) ? 'text-blue-600' : 'text-transparent'">
                          <span class="i-lucide-check w-3.5 h-3.5" />
                        </span>
                        <span class="w-2.5 h-2.5 rounded-full" :style="{ backgroundColor: stage.color }" />
                        <span class="text-n-slate-12 truncate">{{ stage.name }}</span>
                      </button>
                    </template>
                    <div v-if="!availablePipelines.length" class="text-xs text-n-slate-11 text-center py-3">No hay pipelines creados</div>
                  </template>

                  <!-- Responsable -->
                  <template v-else-if="c.type === 'assignee'">
                    <button v-for="ag in availableAgents" :key="ag.id"
                            class="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-n-alpha-black1 text-sm text-left"
                            @click="toggleConditionValue(idx, ag.id)">
                      <span class="w-4 h-4 inline-flex items-center justify-center" :class="c.values.includes(ag.id) ? 'text-blue-600' : 'text-transparent'">
                        <span class="i-lucide-check w-3.5 h-3.5" />
                      </span>
                      <span class="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[10px] flex items-center justify-center font-semibold">{{ (ag.name || '?').slice(0,1) }}</span>
                      <span class="text-n-slate-12 truncate">{{ ag.name }}</span>
                    </button>
                  </template>

                  <!-- Custom -->
                  <template v-else-if="c.type === 'custom'">
                    <input :value="c.values[0] || ''" type="text"
                           placeholder="Ej: pais=DO, cumpleaños=hoy"
                           class="w-full px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-none focus:border-blue-400"
                           @input="setCustomValue(idx, $event.target.value)" />
                  </template>
                </div>
              </div>
              <button class="p-2 text-red-500 hover:bg-red-50 rounded-md" title="Eliminar condición" @click="removeCondition(idx)">
                <span class="i-lucide-trash-2 w-4 h-4" />
              </button>
            </div>
          </div>

          <!-- Agregar condición -->
          <div v-on-click-outside="() => (showAddCondition = false)" class="relative mt-2">
            <button type="button"
                    class="w-full px-3 py-2.5 rounded-lg border border-dashed border-n-weak text-sm text-n-slate-11 hover:border-blue-400 hover:text-blue-600 text-left"
                    @click="showAddCondition = !showAddCondition">
              + Agregar una condición
            </button>
            <div v-if="showAddCondition"
                 class="absolute top-full left-0 right-0 mt-1 z-20 bg-n-background border border-n-weak rounded-lg shadow-xl py-1">
              <button v-for="t in CONDITION_TYPES" :key="t.key"
                      class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-black1"
                      @click="addCondition(t.key)">
                <span class="w-5 text-base">{{ t.icon }}</span>
                <span>{{ t.label.replace(':', '') }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- ═══ Horas activas ═══ -->
        <div>
          <label class="block text-sm text-n-slate-12 mb-1.5">Horas activas:</label>

          <div class="grid grid-cols-2 gap-2 mb-2">
            <button v-for="p in hoursPresets" :key="p.key"
                    class="px-3 py-2 text-sm rounded-lg border text-left flex items-center gap-2 transition"
                    :class="hoursMode === p.key ? 'border-[#10b981] bg-[#10b981]/10 text-[#047857]' : 'border-n-weak text-n-slate-12 hover:border-[#34d399]'"
                    @click="hoursMode = p.key">
              <span class="w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0"
                    :class="hoursMode === p.key ? 'bg-[#10b981]' : 'border border-n-strong'">
                <span v-if="hoursMode === p.key" class="i-lucide-check w-3.5 h-3.5 text-white" />
              </span>
              {{ p.label }}
            </button>
          </div>

          <!-- Sub-panel custom -->
          <div v-if="hoursMode === 'custom'" class="rounded-lg border border-n-weak p-3 space-y-3 bg-n-alpha-black1">
            <div>
              <div class="text-[11px] text-n-slate-11 mb-1.5 font-medium">Días activos:</div>
              <div class="flex gap-1">
                <button v-for="d in DAYS" :key="d.v"
                        class="w-9 h-9 rounded-md text-xs font-semibold transition"
                        :class="hoursDays.includes(d.v) ? 'bg-blue-600 text-white' : 'bg-n-background border border-n-weak text-n-slate-12 hover:border-blue-300'"
                        @click="toggleDay(d.v)">
                  {{ d.l }}
                </button>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <div class="text-[11px] text-n-slate-11 mb-1.5 font-medium">Desde:</div>
                <input v-model="hoursFrom" type="time" class="w-full px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-none focus:border-blue-400" />
              </div>
              <div>
                <div class="text-[11px] text-n-slate-11 mb-1.5 font-medium">Hasta:</div>
                <input v-model="hoursTo" type="time" class="w-full px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-none focus:border-blue-400" />
              </div>
            </div>
            <p class="text-[11px] text-n-slate-11">
              Resumen: <strong>{{ customHoursLabel }}</strong>
            </p>
          </div>
        </div>

        <!-- Toggle: dejar sin respuesta -->
        <div>
          <label class="flex items-center gap-3 cursor-pointer">
            <span class="text-sm text-n-slate-12">Dejar el mensaje sin respuesta</span>
            <span class="relative inline-block w-9 h-5 rounded-full transition" :class="leaveUnanswered ? 'bg-[#10b981]' : 'bg-n-alpha-black3'">
              <span class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition" :class="leaveUnanswered ? 'translate-x-4' : ''" />
            </span>
            <input v-model="leaveUnanswered" type="checkbox" class="sr-only" />
          </label>
          <p class="text-[11px] text-n-slate-11 mt-1.5">Los mensajes que Salesbot responda aparecerán como no contestados</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-n-weak flex items-center justify-between gap-2">
        <button v-if="trigger.id" class="px-3 py-2 text-xs rounded-md text-red-600 hover:bg-red-50" @click="emit('delete', trigger)">Eliminar</button>
        <span v-else></span>
        <div class="flex items-center gap-2">
          <button class="px-4 py-2 text-sm rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-black1" @click="emit('close')">Cancelar</button>
          <button
            class="px-5 py-2 text-sm rounded-lg font-semibold text-white border border-amber-800/40
                   bg-gradient-to-br from-[#F5C842] via-[#D4AF37] to-[#B8860B]
                   hover:from-[#FFD75F] hover:via-[#E5C04A] hover:to-[#C99918]
                   hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/40
                   transition-all"
            @click="save"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
@keyframes modalIn {
  from { opacity: 0; transform: scale(0.95) translateY(8px); }
  to   { opacity: 1; transform: scale(1)   translateY(0);   }
}
</style>
